'use client';

import { useTranslation } from 'next-i18next';
import { BulkOperationProps } from '@type/bulk-operations';
import {
  Button, PopoverStyled, PopoverStyledButton, PopoverStyledPanel,
} from '@components/common';
import { FloatingNavBar } from '@components/features/job-list-content/FloatingNavBar';
import {
  BulkCopyApplicants,
  BulkMessage,
  BulkSendCv,
  BulkValidate,
} from '@components/templates/employer/applicant-list/bulk-operations';
import { EllipsisHorizontalIcon } from '@heroicons/react/24/outline';
import { useBjModal } from '@hooks/common/useBjModal';
import { McCvUploadModal } from '@components/templates/employer/applicant-list/flow-mc/McCvUploadModal';

interface McFlowActionBarProps extends BulkOperationProps {
  getLockedApplicantIds?: () => string[];
  selectedCount?: number;
  onApplicantsColumnRevalidate?: () => void | Promise<unknown>;
}

/**
 * @description Floating action bar used only by the Mission Control flow board.
 * Keeps the MC actions visible without reusing the default flow top toolbar.
 */
export const McFlowActionBar = (props: McFlowActionBarProps) => {
  const {
    locale,
    jobSlug,
    getLockedApplicantIds,
    selectedCount = 0,
    onApplicantsColumnRevalidate,
    ...bulkProps
  } = props;

  const { t } = useTranslation('common');
  const {
    isOpen: isUploadModalOpen,
    openModal: openUploadModal,
    closeModal: closeUploadModal,
  } = useBjModal();
  const hasSelectedApplicants = selectedCount > 0 || Boolean(bulkProps.selectAll);

  return (
    <>
      <FloatingNavBar>
        <div className="flex w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)] items-center gap-3 lg:w-auto lg:max-w-none [&>button:disabled]:!cursor-not-allowed [&>button:disabled]:!bg-surface-50 [&>button:disabled]:!text-ink-medium [&>button:disabled]:!opacity-100 [&>button:disabled]:!shadow-sm [&>button]:!min-w-0 [&>button]:!grow [&>button]:!whitespace-nowrap [&>button]:!rounded-2xl [&>button]:!bg-white [&>button]:!px-5 [&>button]:!py-3 [&>button]:!text-sm [&>button]:shadow-md [&>button]:hover:!no-underline lg:[&>button]:!grow-0">
          <Button
            type="button"
            styling="solid"
            color="light"
            onClick={openUploadModal}
          >
            {t('my-jobs.card.actions.upload-cvs')}
          </Button>

          <BulkValidate
            locale={locale}
            jobSlug={jobSlug}
            getLockedApplicantIds={getLockedApplicantIds}
            hideTriggerIcon
            triggerDisabled={!hasSelectedApplicants}
            {...bulkProps}
          />

          <PopoverStyled className="relative">
            <PopoverStyledButton
              as={Button}
              type="button"
              styling="solid"
              color="light"
              disabled={!hasSelectedApplicants}
              title={t('global.label.actions')}
              aria-label={t('global.label.actions')}
              className="!h-[46px] !w-[46px] !rounded-2xl !bg-white !p-0 !text-sm shadow-md hover:!no-underline disabled:!cursor-not-allowed disabled:!bg-surface-50 disabled:!text-ink-medium disabled:!opacity-100 disabled:!shadow-sm"
            >
              <EllipsisHorizontalIcon className="size-5" />
            </PopoverStyledButton>

            <PopoverStyledPanel
              animation="slideUp"
              position="right"
              className="bottom-full mb-2 flex min-w-[220px] flex-col gap-1 rounded-2xl bg-white p-2 shadow-xl [&_button:hover]:!bg-surface-50 [&_button:hover]:!no-underline [&_button]:!w-full [&_button]:!justify-start [&_button]:!rounded-xl [&_button]:!px-4 [&_button]:!py-3 [&_button]:!text-sm [&_button]:!shadow-none [&_button]:transition-colors"
            >
              <BulkMessage
                locale={locale}
                jobSlug={jobSlug}
                hideTriggerIcon
                triggerDisabled={!hasSelectedApplicants}
                {...bulkProps}
              />

              <BulkCopyApplicants
                locale={locale}
                jobSlug={jobSlug}
                hideTriggerIcon
                triggerDisabled={!hasSelectedApplicants}
                {...bulkProps}
              />

              <BulkSendCv
                locale={locale}
                jobSlug={jobSlug}
                hideTriggerIcon
                triggerDisabled={!hasSelectedApplicants}
                {...bulkProps}
              />
            </PopoverStyledPanel>
          </PopoverStyled>
        </div>
      </FloatingNavBar>

      <McCvUploadModal
        isOpen={isUploadModalOpen}
        onClose={closeUploadModal}
        jobSlug={jobSlug}
        onApplicantsColumnRevalidate={onApplicantsColumnRevalidate}
      />
    </>
  );
};
